import client from "@/client/marketDataApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetClientMarketFundamentalsQueryResponse, GetClientMarketFundamentalsPathParams, GetClientMarketFundamentals404 } from "../types/GetClientMarketFundamentals";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetClientMarketFundamentalsClient = typeof client<GetClientMarketFundamentalsQueryResponse, GetClientMarketFundamentals404, never>;
type GetClientMarketFundamentals = {
    data: GetClientMarketFundamentalsQueryResponse;
    error: GetClientMarketFundamentals404;
    request: never;
    pathParams: GetClientMarketFundamentalsPathParams;
    queryParams: never;
    headerParams: never;
    response: GetClientMarketFundamentalsQueryResponse;
    client: {
        parameters: Partial<Parameters<GetClientMarketFundamentalsClient>[0]>;
        return: Awaited<ReturnType<GetClientMarketFundamentalsClient>>;
    };
};
export const getClientMarketFundamentalsQueryKey = (clientCode: GetClientMarketFundamentalsPathParams["clientCode"], marketCode: GetClientMarketFundamentalsPathParams["marketCode"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode/data/fundamentals", params: { clientCode: clientCode, marketCode: marketCode } }] as const;
export type GetClientMarketFundamentalsQueryKey = ReturnType<typeof getClientMarketFundamentalsQueryKey>;
export function getClientMarketFundamentalsQueryOptions(clientCode: GetClientMarketFundamentalsPathParams["clientCode"], marketCode: GetClientMarketFundamentalsPathParams["marketCode"], options: GetClientMarketFundamentals["client"]["parameters"] = {}) {
    const queryKey = getClientMarketFundamentalsQueryKey(clientCode, marketCode);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetClientMarketFundamentals["data"], GetClientMarketFundamentals["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}/data/fundamentals`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Returns fundamental information for a client market. This includes
the current state of the market, the last timing event, and the
list of instruments.
 * @summary Fundamentals
 * @link /api/clients/:clientCode/markets/:marketCode/data/fundamentals
 */
export function useGetClientMarketFundamentals<TData = GetClientMarketFundamentals["response"], TQueryData = GetClientMarketFundamentals["response"], TQueryKey extends QueryKey = GetClientMarketFundamentalsQueryKey>(clientCode: GetClientMarketFundamentalsPathParams["clientCode"], marketCode: GetClientMarketFundamentalsPathParams["marketCode"], options: {
    query?: Partial<QueryObserverOptions<GetClientMarketFundamentals["response"], GetClientMarketFundamentals["error"], TData, TQueryData, TQueryKey>>;
    client?: GetClientMarketFundamentals["client"]["parameters"];
} = {}): UseQueryResult<TData, GetClientMarketFundamentals["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getClientMarketFundamentalsQueryKey(clientCode, marketCode);
    const query = useQuery({
        ...getClientMarketFundamentalsQueryOptions(clientCode, marketCode, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetClientMarketFundamentals["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getClientMarketFundamentalsSuspenseQueryKey = (clientCode: GetClientMarketFundamentalsPathParams["clientCode"], marketCode: GetClientMarketFundamentalsPathParams["marketCode"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode/data/fundamentals", params: { clientCode: clientCode, marketCode: marketCode } }] as const;
export type GetClientMarketFundamentalsSuspenseQueryKey = ReturnType<typeof getClientMarketFundamentalsSuspenseQueryKey>;
export function getClientMarketFundamentalsSuspenseQueryOptions(clientCode: GetClientMarketFundamentalsPathParams["clientCode"], marketCode: GetClientMarketFundamentalsPathParams["marketCode"], options: GetClientMarketFundamentals["client"]["parameters"] = {}) {
    const queryKey = getClientMarketFundamentalsSuspenseQueryKey(clientCode, marketCode);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetClientMarketFundamentals["data"], GetClientMarketFundamentals["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}/data/fundamentals`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Returns fundamental information for a client market. This includes
the current state of the market, the last timing event, and the
list of instruments.
 * @summary Fundamentals
 * @link /api/clients/:clientCode/markets/:marketCode/data/fundamentals
 */
export function useGetClientMarketFundamentalsSuspense<TData = GetClientMarketFundamentals["response"], TQueryKey extends QueryKey = GetClientMarketFundamentalsSuspenseQueryKey>(clientCode: GetClientMarketFundamentalsPathParams["clientCode"], marketCode: GetClientMarketFundamentalsPathParams["marketCode"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetClientMarketFundamentals["response"], GetClientMarketFundamentals["error"], TData, TQueryKey>>;
    client?: GetClientMarketFundamentals["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetClientMarketFundamentals["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getClientMarketFundamentalsSuspenseQueryKey(clientCode, marketCode);
    const query = useSuspenseQuery({
        ...getClientMarketFundamentalsSuspenseQueryOptions(clientCode, marketCode, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetClientMarketFundamentals["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}